import {
  duplicateWorkoutItem,
  removeWorkoutItem as removeWorkoutItemApi,
  unlinkWorkoutItem,
} from "api-client";
import { useMemo } from "react";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import {
  type WorkoutItemActionsHandler,
  type WorkoutItemActionsProviderProps,
  workoutItemActionsContext,
} from "src/workouts/WorkoutItemActionsContext";

export function ExerciseWorkoutItemActionsProvider(
  props: WorkoutItemActionsProviderProps,
) {
  const workoutEditStore = useWorkoutEditStore();
  const { startSaving } = useAuthenticatedNavigationContext();

  const canUnlink = workoutEditStore.canUnlink(props.workoutItemId);

  const handler = useMemo<WorkoutItemActionsHandler>(() => {
    return {
      canUnlink,
      duplicate: async () => {
        const stop = startSaving();
        const response = await duplicateWorkoutItem({
          path: {
            workoutId: workoutEditStore.workout.id,
            workoutItemId: props.workoutItemId,
          },
        });
        stop();
        workoutEditStore.updateItems(response.data.data.workoutItems);
      },
      delete: async () => {
        const stop = startSaving();
        const response = await removeWorkoutItemApi({
          path: {
            workoutId: workoutEditStore.workout.id,
            workoutItemId: props.workoutItemId,
          },
        });
        stop();
        workoutEditStore.updateItems(response.data.data.workoutItems);
      },
      unlink: canUnlink
        ? async () => {
            const stop = startSaving();

            try {
              const response = await unlinkWorkoutItem({
                path: { workoutId: workoutEditStore.workout.id },
                body: { itemId: props.workoutItemId },
              });

              const data = response.data.data;
              workoutEditStore.applyWorkoutItemChanges(data.operations);

              stop();
            } catch {
              stop("error");
            }
          }
        : undefined,
    };
  }, [workoutEditStore, props.workoutItemId, canUnlink]);

  const value = useMemo(() => {
    return { handler };
  }, [handler]);

  return (
    <workoutItemActionsContext.Provider value={value}>
      {props.children}
    </workoutItemActionsContext.Provider>
  );
}
